/** Max age (exclusive) in days to use pediatric replacement flow: under 18 years. */
export const PEDIATRIC_MAX_AGE_DAYS = 6575;

export function ageDaysFromDob(dob: string | undefined | null): number | null {
  if (!dob?.trim()) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export function isPediatricByDob(dob: string | undefined | null): boolean {
  const days = ageDaysFromDob(dob);
  if (days === null) return false;
  return days < PEDIATRIC_MAX_AGE_DAYS;
}

/** Normalize patient DOB to `YYYY-MM-DD` for `<input type="date">`, or empty string. */
export function dobToDateInputValue(dob: string | undefined | null): string {
  if (!dob?.trim()) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) {
    const m = dob.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const [, mo, day, y] = m;
      return `${y}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type ShowIfCondition = {
  equals?: string;
  includes?: string[];
  min?: number;
  max?: number;
};

export type ShowIf = Record<string, ShowIfCondition>;

export function evaluateShowIf(
  showIf: ShowIf | undefined,
  formValues: Record<string, unknown>,
  ageDays: number | null
): boolean {
  if (!showIf) return true;
  return Object.entries(showIf).every(([key, condition]) => {
    const value =
      key === "child_age_days" ? ageDays : formValues[key];
    if (value === null || value === undefined) return false;

    if (condition.equals !== undefined) return value === condition.equals;

    if (condition.includes !== undefined) {
      return (
        typeof value === "string" && condition.includes.includes(value)
      );
    }

    const num =
      typeof value === "number"
        ? value
        : typeof value === "string"
          ? Number(value)
          : NaN;
    if (key === "child_age_days" && ageDays !== null) {
      const ad = ageDays;
      if (condition.min !== undefined && ad < condition.min) return false;
      if (condition.max !== undefined && ad > condition.max) return false;
      return true;
    }
    if (!Number.isFinite(num)) return false;
    if (condition.min !== undefined && num < condition.min) return false;
    if (condition.max !== undefined && num > condition.max) return false;
    return true;
  });
}

export function serializeIntakeValue(
  value: unknown
): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return value.length ? value.join("|") : null;
  return String(value);
}

export type IntakeFieldDef = {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  note?: string;
  show_if?: ShowIf;
  options?: unknown;
  followUp?: IntakeFieldDef[];
  min?: number;
  max?: number;
  labels?: Record<string, string>;
};

function fieldIsVisible(
  field: IntakeFieldDef,
  formValues: Record<string, unknown>,
  ageDays: number | null
): boolean {
  return evaluateShowIf(field.show_if, formValues, ageDays);
}

type AnyField = IntakeFieldDef;

function isEmptyValue(v: unknown): boolean {
  return (
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && v.length === 0)
  );
}

/** Validate required fields in tree; only visits fields visible under `show_if`. */
export function validateFieldsRecursive(
  fields: AnyField[] | undefined,
  formValues: Record<string, unknown>,
  ageDays: number | null,
  errors: Record<string, string>
): void {
  if (!fields) return;
  for (const f of fields) {
    if (!fieldIsVisible(f, formValues, ageDays)) continue;
    if (f.required && isEmptyValue(formValues[f.id])) {
      errors[f.id] = "This field is required.";
    }
    if (
      f.type === "yes_no_with_followup" &&
      formValues[f.id] === true &&
      f.followUp
    ) {
      validateFieldsRecursive(f.followUp, formValues, ageDays, errors);
    }
  }
}
